import { useEffect, useRef, useState, useCallback } from "react";
import { useParams, useSearchParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { PaperPlaneRight, Paperclip, ShieldCheck, Play, StopCircle, FileText, CircleNotch, ArrowLeft, Stethoscope, Pill } from "@phosphor-icons/react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SESSION_LABEL = { scheduled: "Menunggu Sesi Dimulai", in_progress: "Sesi Berlangsung", ended: "Sesi Berakhir" };

function MediaBubble({ m, fetchBlob }) {
  const [url, setUrl] = useState(null);
  const [err, setErr] = useState(false);
  useEffect(() => {
    let alive = true;
    fetchBlob(m.id).then((u) => { if (alive) setUrl(u); }).catch(() => { if (alive) setErr(true); });
    return () => { alive = false; };
  }, [m.id, fetchBlob]);
  if (err) return <span className="text-xs">Gagal memuat lampiran</span>;
  if (!url) return <CircleNotch size={18} className="animate-spin" />;
  if (m.type === "image") return <img data-testid={`tc-media-image-${m.id}`} src={url} alt="lampiran" onClick={() => window.open(url, "_blank")} className="max-h-64 max-w-full cursor-pointer rounded-xl object-cover" />;
  if (m.type === "video") return <video src={url} controls playsInline className="max-h-72 max-w-full rounded-xl" />;
  return <a data-testid={`tc-media-doc-${m.id}`} href={url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-xs font-bold underline"><FileText size={16} /> {m.file_name || "Dokumen PDF"}</a>;
}

function AdminGate({ room, client, onGranted }) {
  const [alasan, setAlasan] = useState("");
  const req = async () => {
    try { await client().post(`/consult/rooms/${room.id}/admin-access`, { alasan }); toast.success("Akses darurat diberikan (60 menit, diaudit)."); onGranted(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Gagal meminta akses"); }
  };
  return (
    <div className="m-4 rounded-2xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900" data-testid="tc-admin-gate">
      <p className="font-bold">Isi percakapan klinis tidak ditampilkan untuk Admin.</p>
      <p className="mt-1 text-xs">Admin hanya melihat metadata. Super Admin dapat membuka akses darurat dengan alasan tertulis (berlaku 60 menit dan tercatat di audit).</p>
      {room.is_super_admin && <>
      <textarea data-testid="tc-admin-reason" value={alasan} onChange={(e) => setAlasan(e.target.value)} rows={2} placeholder="Alasan akses (min. 10 karakter)" className="mt-2 w-full rounded-lg border border-amber-200 bg-white p-2 text-sm outline-none" />
      <button data-testid="tc-admin-request-access" onClick={req} className="mt-2 rounded-full bg-amber-600 px-4 py-1.5 text-xs font-bold text-white">Minta Akses Darurat</button>
      </>}
    </div>
  );
}

function RxPanel({ rx }) {
  if (!rx.length) return null;
  return (
    <div className="mx-3 mt-2 rounded-2xl border border-pink-100 bg-white p-3" data-testid="tc-rx-panel">
      <p className="mb-1.5 flex items-center gap-1.5 text-xs font-bold text-[#DB2777]"><Pill size={14} weight="duotone" /> Resep & Aturan Pakai</p>
      {rx.map((p) => (
        <div key={p.id} className="mb-2 last:mb-0" data-testid={`tc-rx-${p.id}`}>
          <p className="text-[10px] text-slate-400">{p.prescriber_nama || "Tenaga Kesehatan"} · {p.status === "Diserahkan" ? "Obat sudah diserahkan" : "Diverifikasi farmasi"}{p.obat_total > 0 ? ` · Obat Rp${p.obat_total.toLocaleString("id-ID")} (${p.obat_payment_status === "Lunas" ? "lunas" : "belum dibayar"})` : ""}</p>
          {p.items.map((it, i) => (
            <div key={i} className="mt-1 rounded-lg bg-[#FDF2F8] px-2.5 py-1.5 text-xs text-slate-700">
              <b>{it.nama}</b>{it.jumlah ? ` · ${it.jumlah}` : ""}{it.dosis ? ` · ${it.dosis}` : ""}{it.frekuensi ? ` · ${it.frekuensi}` : ""}{it.rute ? ` · ${it.rute}` : ""}{it.durasi ? ` · ${it.durasi}` : ""}
              {it.aturan_pakai && <p className="text-[11px] text-slate-600">Aturan pakai: {it.aturan_pakai}</p>}
              {it.catatan && <p className="text-[11px] text-slate-500">Catatan: {it.catatan}</p>}
            </div>
          ))}
          {p.note && <p className="mt-1 text-[11px] text-slate-500">{p.note}</p>}
        </div>
      ))}
    </div>
  );
}

export default function ConsultRoom() {
  const { roomId } = useParams();
  const [sp] = useSearchParams();
  const token = sp.get("t") || "";
  const nav = useNavigate();
  const [room, setRoom] = useState(null);
  const [msgs, setMsgs] = useState([]);
  const [contentErr, setContentErr] = useState("");
  const [rx, setRx] = useState([]);
  const [err, setErr] = useState("");
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);
  const bottom = useRef(null);

  const client = useCallback(() => {
    const headers = {};
    if (token) headers["X-Room-Token"] = token;
    else { const jwt = localStorage.getItem("token"); if (jwt) headers.Authorization = `Bearer ${jwt}`; }
    return axios.create({ baseURL: API, headers });
  }, [token]);

  const load = useCallback(async () => {
    const c = client();
    let rm;
    try { const r = await c.get(`/consult/rooms/${roomId}`); rm = r.data.room; setRoom(rm); setErr(""); }
    catch (e) { setErr(e?.response?.data?.detail || "Tidak dapat memuat ruang konsultasi."); return; }
    if (rm.viewer === "admin" && !rm.admin_content_access) { setContentErr("Isi percakapan klinis tidak tersedia untuk admin tanpa akses darurat."); return; }
    try { const m = await c.get(`/consult/rooms/${roomId}/messages`); setMsgs(m.data.messages); setContentErr(""); }
    catch (e) { setContentErr(e?.response?.data?.detail || "Isi percakapan tidak tersedia."); return; }
    c.get(`/consult/rooms/${roomId}/prescriptions`).then((p) => setRx(p.data.prescriptions)).catch(() => {});
  }, [client, roomId]);

  useEffect(() => { load(); const id = setInterval(() => { if (!document.hidden) load(); }, 5000); return () => clearInterval(id); }, [load]);
  useEffect(() => { bottom.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs.length]);
  useEffect(() => { document.title = "Konsultasi Online — GBS"; }, []);

  const fetchBlob = useCallback(async (mid) => {
    const r = await client().get(`/consult/rooms/${roomId}/media/${mid}`, { responseType: "blob" });
    return URL.createObjectURL(r.data);
  }, [client, roomId]);

  const act = async (fn, okMsg) => {
    setBusy(true);
    try { const r = await fn(); if (okMsg) toast.success(okMsg); await load(); return r; }
    catch (e) { toast.error(e?.response?.data?.detail || "Gagal"); }
    finally { setBusy(false); }
  };
  const send = () => { const t = text.trim(); if (!t) return; act(() => client().post(`/consult/rooms/${roomId}/messages`, { text: t })).then((r) => { if (r) setText(""); }); };
  const upload = (e) => {
    const f = e.target.files?.[0]; e.target.value = ""; if (!f) return;
    const fd = new FormData(); fd.append("file", f);
    act(() => client().post(`/consult/rooms/${roomId}/media`, fd));
  };
  const start = () => act(() => client().post(`/consult/rooms/${roomId}/start`), "Sesi dimulai");
  const end = () => { if (window.confirm("Akhiri sesi konsultasi ini?")) act(() => client().post(`/consult/rooms/${roomId}/end`), "Sesi diakhiri"); };
  const openRme = async () => {
    try { const r = await client().get(`/consult/rooms/${roomId}/encounter`); nav(`/rekam-medis/${r.data.encounter_id}`); }
    catch (e) { toast.error(e?.response?.data?.detail || "Encounter belum tersedia"); }
  };

  if (err && !room) return <div className="mx-auto mt-16 max-w-sm rounded-2xl border border-teal-100 bg-white p-6 text-center text-sm text-slate-600" data-testid="tc-error">{err}</div>;
  if (!room) return <div className="mt-20 text-center text-sm text-slate-400">Memuat…</div>;
  const viewer = room.viewer;
  const canWrite = viewer !== "admin" && room.status === "open" && room.session_status === "in_progress";

  return (
    <div className="mx-auto flex h-[100dvh] max-w-2xl flex-col bg-[#F4FBFA]" data-testid="consult-room">
      {!token && (
        <div className="border-b border-teal-100 bg-white px-4 py-2">
          <button data-testid="tc-back" onClick={() => nav(viewer === "admin" ? "/konsultasi-admin" : "/konsultasi-saya")} className="inline-flex items-center gap-1 text-xs font-bold text-teal-700"><ArrowLeft size={14} /> Kembali</button>
        </div>
      )}
      <header className="flex items-center gap-3 bg-[#0F4C47] px-4 py-3 text-white">
        <div className="flex h-10 w-10 items-center justify-center rounded-full bg-white/15"><Stethoscope size={20} /></div>
        <div className="min-w-0 flex-1">
          <p className="truncate font-heading text-sm font-extrabold" data-testid="tc-peer-name">{viewer === "client" ? (room.assigned_staff_name || "Tenaga Kesehatan GBS") : room.client_name}</p>
          <p className="text-[11px] text-white/75">Konsultasi Online • {room.service || "-"} • {room.tanggal_kunjungan} {room.jam_kunjungan}</p>
        </div>
        <span className="rounded-full bg-white/15 px-2 py-0.5 text-[10px] font-bold" data-testid="tc-session-status">{room.status === "open" ? SESSION_LABEL[room.session_status] : room.status === "archived" ? "Diarsipkan" : "Ditutup"}</span>
      </header>
      <div className="flex items-center gap-2 bg-teal-50 px-3 py-1.5 text-[11px] text-teal-800"><ShieldCheck size={14} weight="bold" /> Ruang konsultasi resmi GBS. Jangan membagikan kontak pribadi. Koneksi terputus tidak mengakhiri sesi.</div>

      {viewer === "staff" && (
        <div className="flex flex-wrap gap-2 border-b border-teal-100 bg-white px-3 py-2">
          {room.session_status === "scheduled" && room.status === "open" && <button data-testid="tc-start-session" disabled={busy} onClick={start} className="inline-flex items-center gap-1 rounded-full bg-teal-600 px-3 py-1.5 text-xs font-bold text-white disabled:opacity-50"><Play size={14} weight="fill" /> Mulai Sesi</button>}
          {room.session_status === "in_progress" && <button data-testid="tc-end-session" disabled={busy} onClick={end} className="inline-flex items-center gap-1 rounded-full bg-rose-600 px-3 py-1.5 text-xs font-bold text-white disabled:opacity-50"><StopCircle size={14} weight="fill" /> Akhiri Sesi</button>}
          {room.has_encounter && <button data-testid="tc-open-rme" onClick={openRme} className="inline-flex items-center gap-1 rounded-full bg-[#E6F4F1] px-3 py-1.5 text-xs font-bold text-teal-800"><FileText size={14} /> Buka RME</button>}
          <p className="w-full text-[10px] text-slate-400">RME diisi manual oleh tenaga kesehatan; isi chat tidak diubah otomatis menjadi SOAP.</p>
        </div>
      )}

      {contentErr ? (
        viewer === "admin" ? <AdminGate room={room} client={client} onGranted={load} /> : <p className="m-4 text-sm text-slate-500" data-testid="tc-content-error">{contentErr}</p>
      ) : (
        <>
        <RxPanel rx={rx} />
        <div className="flex-1 space-y-2 overflow-y-auto px-3 py-3" data-testid="tc-messages">
          {msgs.map((m) => {
            if (m.sender_kind === "system") return <div key={m.id} className="mx-auto w-fit max-w-[90%] rounded-full bg-slate-100 px-3 py-1 text-center text-[11px] text-slate-500" data-testid="tc-system-msg">{m.text}</div>;
            const mine = m.sender_kind === viewer;
            return (
              <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[80%] min-w-0 break-words rounded-2xl px-3 py-2 text-sm ${mine ? "bg-teal-700 text-white" : "border border-teal-100 bg-white text-slate-700"}`} data-testid="tc-msg">
                  {!mine && <p className="mb-0.5 text-[10px] font-bold opacity-70">{m.sender_name}</p>}
                  {m.type === "text" ? m.text : <MediaBubble m={m} fetchBlob={fetchBlob} />}
                  <p className={`mt-0.5 text-[9px] ${mine ? "text-white/70" : "text-slate-400"}`}>{(m.created_at_wita || "").slice(11, 16)} WITA</p>
                </div>
              </div>
            );
          })}
          <div ref={bottom} />
        </div>
        </>
      )}

      {viewer !== "admin" && (
        <>
          {!canWrite && <div className="bg-slate-100 px-3 py-2 text-center text-xs text-slate-500" data-testid="tc-readonly-note">{room.status !== "open" ? "Ruang konsultasi telah ditutup (hanya baca)." : room.session_status === "scheduled" ? "Sesi akan dimulai oleh tenaga kesehatan sesuai jadwal." : "Sesi konsultasi telah berakhir (hanya baca)."}</div>}
          <input ref={fileRef} type="file" accept="image/*,video/*,application/pdf" hidden onChange={upload} data-testid="tc-file-input" />
          <div className="flex items-center gap-1.5 border-t border-teal-100 bg-white px-2 py-2">
            <button data-testid="tc-attach" disabled={!canWrite || busy} onClick={() => fileRef.current?.click()} className="rounded-full bg-[#E6F4F1] p-2 text-teal-700 disabled:opacity-40"><Paperclip size={18} /></button>
            <input data-testid="tc-input" disabled={!canWrite} value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()} placeholder="Tulis pesan…" className="min-w-0 flex-1 rounded-full border border-teal-100 bg-[#FAFEFD] px-3 py-2 text-base outline-none disabled:opacity-40" />
            <button data-testid="tc-send" disabled={!canWrite || busy} onClick={send} className="rounded-full bg-teal-700 p-2 text-white disabled:opacity-40"><PaperPlaneRight size={18} weight="fill" /></button>
          </div>
        </>
      )}
    </div>
  );
}
